import React from "react";
import { Box, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { IHoliday } from "../../../../../../interfaces/HolidayInterface";
import { CalendarFunctions } from "../../../../../../components/Calendar";

interface HolidayCalendarProps {
  bsYear: number;
  // 1-indexed (1 = Baisakh)
  bsMonth: number;
  // AD in ISO strings
  yearlyHolidays: IHoliday[];
  onDateClick: (adDate: Date) => void;
  onMonthChange: (bsYear: number, bsMonth: number) => void;
}

const nepaliWeekdays = ["आइत", "सोम", "मंगल", "बुध", "बिही", "शुक्र", "शनि"];
const nepaliMonths = [
  "बैशाख",
  "जेठ",
  "असार",
  "साउन",
  "भदौ",
  "आश्विन",
  "कार्तिक",
  "मंसिर",
  "पुष",
  "माघ",
  "फाल्गुण",
  "चैत्र"
];

// eslint-disable-next-line import/prefer-default-export
export function HolidayCalendar({
  bsYear,
  bsMonth,
  yearlyHolidays,
  onDateClick,
  onMonthChange
}: HolidayCalendarProps): React.ReactElement {
  const daysInMonth = CalendarFunctions.getBsMonthDays(bsYear, bsMonth);
  const firstAdDate = CalendarFunctions.convertBStoAD(bsYear, bsMonth, 1);
  const firstDayOfWeek = firstAdDate.getDay();

  // for date grid
  const days: (null | { adDate: Date; bsDate: number })[] = [];
  for (let i = 0; i < firstDayOfWeek; i += 1) days.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    const adDate = CalendarFunctions.convertBStoAD(bsYear, bsMonth, d);
    days.push({ adDate, bsDate: d });
  }
  while (days.length % 7 !== 0) days.push(null);

  // for navigation
  const handlePrev = () => {
    let newMonth = bsMonth - 1;
    let newYear = bsYear;
    if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    }
    onMonthChange(newYear, newMonth);
  };

  const handleNext = () => {
    let newMonth = bsMonth + 1;
    let newYear = bsYear;
    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    }
    onMonthChange(newYear, newMonth);
  };

  // helper functions
  const isHoliday = (adDate: Date) =>
    yearlyHolidays.some((h) => h.date.slice(0, 10) === adDate.toISOString().slice(0, 10));

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: 20,
          fontWeight: 600,
          mb: 2
        }}
      >
        <Box>
          <IconButton onClick={handlePrev}>
            <ChevronLeftIcon />
          </IconButton>
          <span>
            {nepaliMonths[bsMonth - 1]} {CalendarFunctions.toDevanagariDigits(bsYear)}
          </span>
          <IconButton onClick={handleNext} sx={{ minWidth: 36, px: 1 }}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "2px"
        }}
      >
        {/* week days */}
        {nepaliWeekdays.map((label) => (
          <Box
            key={label}
            sx={{ textAlign: "center", fontWeight: 500, color: "#888", py: 1, bgcolor: "#f7f7f7" }}
          >
            {label}
          </Box>
        ))}

        {/* days cell */}
        {days.map((cell, idx) => {
          if (!cell) {
            return (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={`empty-${bsYear}-${bsMonth}-${idx}`}
                sx={{
                  bgcolor: "#f7f7f7",
                  minHeight: 60,
                  borderRadius: 1,
                  border: "1px solid #ededed",
                  outline: "none"
                }}
              />
            );
          }

          const { adDate, bsDate } = cell;
          const holiday = isHoliday(adDate);

          let bgcolor = "#fff";
          let color = "inherit";
          let fontWeight: number | string = "inherit";

          if (holiday) {
            bgcolor = "#FFCCCC";
            color = "#d32f2f";
            fontWeight = 700;
          }

          const holidayObj = yearlyHolidays.find(
            (h) => h.date.slice(0, 10) === adDate.toISOString().slice(0, 10)
          );

          return (
            <Box
              key={adDate.toISOString()}
              sx={{
                minHeight: 60,
                bgcolor,
                color,
                p: 1,
                cursor: "pointer",
                borderRadius: 1,
                fontWeight,
                transition: "background 0.2s",
                position: "relative",
                border: "1px solid #ededed",
                outline: "none",
                "&:hover": { bgcolor: holiday ? "#ffd6d6" : "#e6f0ff" }
              }}
              role="button"
              onClick={(e) => {
                // If the icon was clicked, don't open the modal
                if ((e.target as HTMLElement).closest(".weekend-icon")) return;
                onDateClick(adDate);
              }}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onDateClick(adDate)}
            >
              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <span>{CalendarFunctions.toDevanagariDigits(bsDate)}</span>

                {holidayObj && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 11,
                      fontWeight: 500
                    }}
                  >
                    {holidayObj.name}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
